import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import { toast } from "sonner";
import PartnerLayout from "./PartnerLayout";
import { Storefront, PaperPlaneTilt, Paperclip as PaperclipIcon } from "@phosphor-icons/react";

const STATUS_TABS = [
    { key: "", label: "All" },
    { key: "submitted", label: "Submitted" },
    { key: "shortlisted", label: "Shortlisted" },
    { key: "interviewing", label: "Interviewing" },
    { key: "offered", label: "Offered" },
    { key: "hired", label: "Hired" },
    { key: "rejected", label: "Rejected" },
];

const STATUS_TONE = {
    submitted: "bg-blue-50 text-blue-700 border-blue-200",
    viewed: "bg-purple-50 text-purple-700 border-purple-200",
    shortlisted: "bg-amber-50 text-amber-700 border-amber-200",
    interviewing: "bg-cyan-50 text-cyan-700 border-cyan-200",
    offered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    hired: "bg-emerald-100 text-emerald-800 border-emerald-300",
    rejected: "bg-red-50 text-red-700 border-red-200",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
};

export default function PartnerSubmissions() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (statusFilter) params.status = statusFilter;
            const r = await api.get("/partner/me/submissions", { params });
            setRows(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch (e) {
            toast.error(describeAxiosError(e));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    return (
        <PartnerLayout>
            <div className="space-y-6" data-testid="partner-submissions-page">
                <header>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0891B2]">My submissions</p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A] mt-1">
                        {total > 0 ? `${total} candidate${total === 1 ? "" : "s"} in the pipeline` : "Submissions"}
                    </h1>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        Track how the recruiter is progressing each candidate. Status updates in real time.
                    </p>
                </header>

                {/* Status filter tabs */}
                <div className="border-b border-[#E5E5E5] flex items-center gap-1 sm:gap-2 overflow-x-auto -mx-4 sm:-mx-0 px-4 sm:px-0"
                     data-testid="partner-submissions-tabs">
                    {STATUS_TABS.map((t) => {
                        const active = statusFilter === t.key;
                        return (
                            <button
                                key={t.key || "all"}
                                type="button"
                                data-testid={`partner-subs-tab-${t.key || "all"}`}
                                onClick={() => setStatusFilter(t.key)}
                                className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                                    active
                                        ? "border-[#0891B2] text-[#0A0A0A]"
                                        : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {loading && <div className="text-sm text-[#A3A3A3]">Loading…</div>}

                {!loading && rows.length === 0 && (
                    <div className="bg-white border border-[#E5E5E5] p-10 text-center space-y-3">
                        <p className="text-sm text-[#525252]">
                            {statusFilter
                                ? `No submissions in "${statusFilter}" yet.`
                                : "You haven't submitted any candidates yet."}
                        </p>
                        <Link
                            to="/partner/marketplace"
                            data-testid="partner-subs-empty-cta"
                            className="inline-flex items-center gap-2 bg-[#0891B2] hover:bg-[#0e7490] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider"
                        >
                            <Storefront size={12} weight="bold" /> Browse open roles
                        </Link>
                    </div>
                )}

                {!loading && rows.length > 0 && (
                    <>
                    {/* Mobile cards */}
                    <div className="sm:hidden space-y-3">
                        {rows.map((s) => (
                            <div key={`m-${s.id}`}
                                 data-testid={`partner-sub-card-${s.id}`}
                                 className="bg-white border border-[#E5E5E5] p-4">
                                <div className="flex items-start gap-3 mb-2">
                                    {s.cv_thumb_url ? (
                                        <img
                                            src={s.cv_thumb_url}
                                            alt="CV preview"
                                            data-testid={`partner-sub-thumb-card-${s.id}`}
                                            className="w-12 h-16 object-cover border border-[#E5E5E5] shrink-0 bg-[#FAFAFA]"
                                            loading="lazy"
                                        />
                                    ) : s.has_cv ? (
                                        <div className="w-12 h-16 border border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-center shrink-0">
                                            <PaperclipIcon size={16} weight="bold" className="text-[#A3A3A3]" />
                                        </div>
                                    ) : null}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-bold truncate">{s.candidate_name}</p>
                                        <p className="text-[11px] text-[#A3A3A3] truncate">for {s.job_title}</p>
                                        {s.has_cv && (
                                            <span data-testid={`partner-sub-cv-card-${s.id}`}
                                                  className="inline-flex items-center gap-1 mt-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                                                <PaperclipIcon size={9} weight="bold" /> CV attached
                                            </span>
                                        )}
                                    </div>
                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border shrink-0 ${STATUS_TONE[s.status] || STATUS_TONE.submitted}`}>
                                        {s.status}
                                    </span>
                                </div>
                                <div className="pt-2 border-t border-[#F5F5F5] grid grid-cols-2 gap-2 text-[11px] text-[#525252]">
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Email</p>
                                        <p className="truncate">{s.candidate_email}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Phone</p>
                                        <p>{s.candidate_phone}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Submitted</p>
                                        <p>{fmtDate(s.submitted_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Last update</p>
                                        <p>{fmtDate(s.updated_at)}</p>
                                    </div>
                                </div>
                                {s.notes && (
                                    <div className="mt-2 pt-2 border-t border-[#F5F5F5]">
                                        <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] mb-1">Your note</p>
                                        <p className="text-xs text-[#525252]">{s.notes}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                                <tr className="text-left">
                                    {["Candidate", "Role", "Contact", "Status", "Submitted"].map((h) => (
                                        <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((s) => (
                                    <tr key={s.id}
                                        data-testid={`partner-sub-row-${s.id}`}
                                        className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {s.cv_thumb_url ? (
                                                    <img
                                                        src={s.cv_thumb_url}
                                                        alt="CV preview"
                                                        data-testid={`partner-sub-thumb-${s.id}`}
                                                        className="w-10 h-14 object-cover border border-[#E5E5E5] shrink-0 bg-[#FAFAFA]"
                                                        loading="lazy"
                                                    />
                                                ) : s.has_cv ? (
                                                    <div className="w-10 h-14 border border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-center shrink-0">
                                                        <PaperclipIcon size={14} weight="bold" className="text-[#A3A3A3]" />
                                                    </div>
                                                ) : null}
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-bold">{s.candidate_name}</p>
                                                        {s.has_cv && !s.cv_thumb_url && (
                                                            <span data-testid={`partner-sub-cv-${s.id}`}
                                                                  className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                                                                <PaperclipIcon size={9} weight="bold" /> CV
                                                            </span>
                                                        )}
                                                    </div>
                                                    {s.notes && (
                                                        <p className="text-[11px] text-[#A3A3A3] mt-0.5 line-clamp-1">"{s.notes}"</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-[#0A0A0A]">{s.job_title}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-[11px] text-[#525252]">{s.candidate_email}</p>
                                            <p className="text-[11px] text-[#A3A3A3]">{s.candidate_phone}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border ${STATUS_TONE[s.status] || STATUS_TONE.submitted}`}>
                                                {s.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-[11px] text-[#525252]">
                                            {fmtDate(s.submitted_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}
            </div>
        </PartnerLayout>
    );
}
